import {
  Icon,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Text,
} from "@salestrak/ui";
import React from "react";

import { useSidebarNav } from "../use-sidebar-nav";
import { SidebarCountBadge } from "./sidebar-count-badge";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  usePermission,
  useViewerCanBrowseMembers,
} from "#app/lib/hooks/use-permission";

// The workspace pool is the same set the directory views render (all member
// statuses; team privacy is enforced server-side), and its fetch limits
// (100 teams / 500 users) sit above the badge's 99+ cap, so these counts
// never lie.

/** Teams' primary destinations: All Teams and All Members — the standalone
 *  `/teams` and `/members` directory pages — and the Org Chart. The member
 *  surfaces (All Members, Org Chart) are withheld from a share-only viewer,
 *  whose routes deny them too (RequireMemberBrowse). Analytics — the cross-team
 *  rollup — holds its slot for a team manager while unbuilt, in the disabled
 *  treatment the AI section's unbuilt rows wear, with "Coming soon" on hover. Invite Members lives only in
 *  the header "+" menu; the destination list carries no action rows. */
export function TeamsNavSection() {
  const { navigate, isActive } = useSidebarNav();
  const { teams, users } = useWorkspaceData();
  const canBrowseMembers = useViewerCanBrowseMembers();
  const canManageTeams = usePermission("manage.teams");

  return (
    <SidebarGroup>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            // Exact: a singular team (`/team/[id]`) must not light this row.
            isActive={isActive("/teams", true)}
            onClick={() => navigate("/teams")}
          >
            <Icon name="users" />
            <Text truncate>All Teams</Text>
          </SidebarMenuButton>
          <SidebarCountBadge count={teams.length} />
        </SidebarMenuItem>
        {canBrowseMembers ? (
          <>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={isActive("/members", true)}
                onClick={() => navigate("/members")}
              >
                <Icon name="square-user-round" />
                <Text truncate>All Members</Text>
              </SidebarMenuButton>
              <SidebarCountBadge count={users.length} />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={isActive("/org-chart", true)}
                onClick={() => navigate("/org-chart")}
              >
                <Icon name="network" />
                <Text truncate>Org Chart</Text>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </>
        ) : null}
        {canManageTeams ? (
          <SidebarMenuItem>
            {/* The reason rides a tooltip that shows at every sidebar width
                (the button's own tooltip shows only when collapsed), and
                doubles as the row's accessible description. */}
            <SidebarMenuButton
              aria-disabled
              aria-description="Coming soon"
              tooltip={{ children: "Coming soon", hidden: false }}
            >
              <Icon name="wifi" />
              <Text truncate>Analytics</Text>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ) : null}
      </SidebarMenu>
    </SidebarGroup>
  );
}
